import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import Icon from "../components/AppIcon";
import CryptoIcon from "../components/CryptoIcon";


const ROUTE_MAP: Record<string, string> = {
  home: "/",
  about: "/about",
  academy: "/academy",
  trade: "/trade",
  vault: "/vault",
  testimonials: "/testimonials",
  login: "/login",
  register: "/register",
  dashboard: "/dashboard",
};

const PARTNERS = [
  { name: "Coinbase", logo: "/assets/partners/coinbase.svg", wordmark: true },
  { name: "Binance", logo: "/assets/partners/binance.svg" },
  { name: "Kraken", logo: "/assets/partners/kraken.svg" },
  { name: "Ledger", logo: "/assets/partners/ledger.svg", wordmark: true },
  { name: "MetaMask", logo: "/assets/partners/metamask.svg" },
  { name: "Chainlink", logo: "/assets/partners/chainlink.svg" },
];

const HERO_CRYPTOS = [
  { symbol: "BTC", name: "Bitcoin" },
  { symbol: "ETH", name: "Ethereum" },
  { symbol: "SOL", name: "Solana" },
  { symbol: "BNB", name: "BNB" },
  { symbol: "XRP", name: "XRP" },
  { symbol: "ADA", name: "Cardano" },
  { symbol: "USDT", name: "Tether" },
  { symbol: "USDC", name: "USD Coin" },
  { symbol: "DOGE", name: "Dogecoin" },
  { symbol: "DOT", name: "Polkadot" },
];

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { t } = useLanguage();

  useEffect(() => {
    document.title = "Acebarter | Crypto Investment Platform";
  }, []);

  const handleNavigate = (page: string) => {
    const path = ROUTE_MAP[page] || `/${page}`;
    navigate(path);
  };

  const FEATURES = [
    {
      icon: "BookOpen",
      title: t("home.featureLearnTitle"),
      desc: t("home.featureLearnDesc"),
      action: "academy",
    },
    {
      icon: "ShieldCheck",
      title: t("home.featureSecureTitle"),
      desc: t("home.featureSecureDesc"),
      action: "vault",
    },
    {
      icon: "TrendingUp",
      title: t("home.featureMarketsTitle"),
      desc: t("home.featureMarketsDesc"),
      action: "academy",
    },
  ];

  const JOURNEY_STEPS = [
    { stage: 1, title: t("home.stageBasics"), icon: "Sprout" },
    { stage: 2, title: t("home.stageWallet"), icon: "Wallet" },
    { stage: 3, title: t("home.stageBuying"), icon: "CreditCard" },
    { stage: 4, title: t("home.stageMarkets"), icon: "BarChart3" },
    { stage: 5, title: t("home.stageInvesting"), icon: "Target" },
  ];

  const STATS = [
    { value: "100K+", label: t("home.statActiveInvestors"), icon: "Users" },
    { value: "50+", label: t("home.statInvestmentPlans"), icon: "Layers" },
    { value: "$2B+", label: t("home.statAssetsSecured"), icon: "Lock" },
    { value: "99.9%", label: t("home.statUptime"), icon: "Zap" },
  ];

  /* For logged-in users, name or first part of email */
  const displayName = user?.name || user?.email?.split("@")[0] || "";
  const greeting = displayName
    ? displayName.charAt(0).toUpperCase() + displayName.slice(1)
    : "";

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ============================================================
          HERO — Auth-aware: logged-in users see welcome + dashboard CTA
         ============================================================ */}
      <section className="relative pt-16 pb-24 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-accent/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-60 -left-40 w-[500px] h-[500px] bg-accent/3 rounded-full blur-3xl" />
          <div className="absolute top-1/3 left-1/4 w-2 h-2 bg-accent/40 rounded-full animate-pulse" />
          <div
            className="absolute top-1/4 right-1/3 w-1.5 h-1.5 bg-accent/30 rounded-full animate-pulse"
            style={{ animationDelay: "1.5s" }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Auth-aware badge */}
          {isAuthenticated ? (
            <div className="inline-flex items-center gap-2.5 px-4 py-2 bg-accent/10 border border-accent/20 rounded-full text-sm font-medium text-accent mb-8">
              <div className="w-6 h-6 rounded-full bg-accent/20 flex items-center justify-center">
                <span className="text-xs font-bold text-accent">
                  {(user?.name || user?.email || "U").charAt(0).toUpperCase()}
                </span>
              </div>
              {t("home.welcomeBack")}{greeting}
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 border border-accent/20 rounded-full text-sm font-medium text-accent mb-8">
              <Icon name="TrendingUp" size={14} className="text-accent" />
              {t("home.trustedBadge")}
            </div>
          )}

          <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold leading-[1.1] tracking-tight mb-6">
            {isAuthenticated ? (
              <>
                {t("home.heroTitleAuth1")}{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent via-accent-light to-accent">
                  {t("home.heroTitleAuth2")}
                </span>
                <br className="hidden sm:block" />
                {t("home.heroTitleAuth3")}
              </>
            ) : (
              <>
                {t("home.heroTitle1")}{" "}
                <br className="hidden sm:block" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent via-accent-light to-accent">
                  {t("home.heroTitle2")}
                </span>
              </>
            )}
          </h1>

          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
            {isAuthenticated
              ? t("home.heroSubAuth")
              : t("home.heroSub")}
          </p>

          {/* Auth-aware CTA buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {isAuthenticated ? (
              <>
                <button
                  onClick={() => handleNavigate("dashboard")}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent hover:bg-accent-dark text-accent-foreground font-semibold rounded-xl text-lg transition-colors shadow-glow hover:shadow-glow-lg"
                >
                  <Icon name="LayoutDashboard" size={20} />
                  {t("home.goToDashboard")}
                </button>
                <button
                  onClick={() => handleNavigate("trade")}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-card border border-primary/10 hover:border-accent/30 text-foreground font-semibold rounded-xl text-lg transition-colors"
                >
                  <Icon name="TrendingUp" size={20} className="text-accent" />
                  {t("home.tradeNow")}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => handleNavigate("register")}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent hover:bg-accent-dark text-accent-foreground font-semibold rounded-xl text-lg transition-colors shadow-glow hover:shadow-glow-lg"
                >
                  {t("home.startInvesting")}
                  <Icon name="ArrowRight" size={20} />
                </button>
                <button
                  onClick={() => handleNavigate("vault")}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-card border border-primary/10 hover:border-accent/30 text-foreground font-semibold rounded-xl text-lg transition-colors"
                >
                  <Icon name="Shield" size={20} className="text-accent" />
                  {t("home.exploreSecurity")}
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ============ PARTNERS ============ */}
      <section className="py-16 border-y border-primary/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-8">
            {t("home.partneringWith")}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
            {PARTNERS.map((p, i) => (
              <div
                key={i}
                className="flex items-center gap-2 opacity-70 hover:opacity-100 transition-opacity"
              >
                <img
                  src={p.logo}
                  alt={p.name}
                  className={`object-contain ${
                    p.wordmark ? "h-5 md:h-6" : "h-7 md:h-8"
                  }`}
                  loading="lazy"
                />
                {!p.wordmark && (
                  <span className="text-sm font-bold tracking-tight text-muted-foreground">
                    {p.name}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ STATS BAR ============ */}
      <section className="py-14 bg-card/50 border-b border-primary/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {STATS.map((s, i) => (
              <div
                key={i}
                className="text-center p-5 bg-background/50 rounded-2xl border border-primary/10"
              >
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-accent/10 flex items-center justify-center">
                  <Icon name={s.icon} size={20} className="text-accent" />
                </div>
                <div className="text-2xl font-bold text-foreground font-data">
                  {s.value}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SUPPORTED CRYPTOS ============ */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              {t("home.tradeTopCryptos")}
            </h2>
            <p className="text-muted-foreground mt-3">
              {t("home.tradeTopCryptosSub")}
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3 md:gap-4">
            {HERO_CRYPTOS.map((coin) => (
              <div
                key={coin.symbol}
                className="flex items-center gap-2.5 px-5 py-3 bg-card/50 border border-primary/10 rounded-2xl hover:border-accent/20 transition-all"
              >
                <CryptoIcon
                  symbol={coin.symbol}
                  size={32}
                  className="rounded-lg"
                />
                <div>
                  <span className="font-bold text-foreground text-sm">
                    {coin.symbol}
                  </span>
                  <p className="text-[11px] text-muted-foreground leading-tight">
                    {coin.name}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <button
              onClick={() => handleNavigate("trade")}
              className="inline-flex items-center gap-2 text-accent text-sm font-semibold hover:underline"
            >
              {t("home.viewAllAssets")}
              <Icon name="ArrowRight" size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* ============ FEATURES ============ */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              {t("home.whyChoose")}
            </h2>
            <p className="text-muted-foreground mt-3 max-w-lg mx-auto">
              {t("home.whyChooseSub")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <button
                key={i}
                onClick={() => handleNavigate(f.action)}
                className="text-left group p-8 bg-card/50 backdrop-blur-sm border border-primary/10 rounded-2xl hover:border-accent/30 transition-all"
              >
                <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center mb-5 group-hover:bg-accent/15 transition-colors">
                  <Icon name={f.icon} size={26} className="text-accent" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">
                  {f.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {f.desc}
                </p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-accent text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                  {t("home.learnMore")} <Icon name="ArrowRight" size={14} />
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ============ JOURNEY ============ */}
      <section className="py-24 bg-card/30 border-y border-primary/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              {t("home.journeyTitle")}
            </h2>
            <p className="text-muted-foreground mt-3">
              {t("home.journeySub")}
            </p>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-3 md:gap-0">
            {JOURNEY_STEPS.map((step, i) => (
              <React.Fragment key={step.stage}>
                <div className="flex flex-col items-center w-28 md:w-32">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent/20 to-accent/5 border border-accent/20 flex items-center justify-center mb-3">
                    <Icon name={step.icon} size={28} className="text-accent" />
                  </div>
                  <p className="text-sm font-semibold text-foreground text-center">
                    {step.title}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {t("home.stage")} {step.stage}
                  </p>
                </div>
                {i < JOURNEY_STEPS.length - 1 && (
                  <div className="hidden md:flex items-center px-2">
                    <Icon
                      name="ChevronRight"
                      size={20}
                      className="text-primary/20"
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() =>
                handleNavigate(isAuthenticated ? "academy" : "register")
              }
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent hover:bg-accent-dark text-accent-foreground font-semibold rounded-xl transition-colors"
            >
              {isAuthenticated ? t("home.continueLearning") : t("home.startYourJourney")}
              <Icon name="ArrowRight" size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              {t("home.howItWorks")}
            </h2>
            <p className="text-muted-foreground mt-3">
              {t("home.howItWorksSub")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                step: "01",
                icon: "UserPlus",
                title: t("home.step1Title"),
                desc: t("home.step1Desc"),
              },
              {
                step: "02",
                icon: "ArrowDownCircle",
                title: t("home.step2Title"),
                desc: t("home.step2Desc"),
              },
              {
                step: "03",
                icon: "TrendingUp",
                title: t("home.step3Title"),
                desc: t("home.step3Desc"),
              },
            ].map((item, i) => (
              <div key={i} className="relative text-center">
                <div className="text-5xl font-black text-accent/10 mb-4 font-data">
                  {item.step}
                </div>
                <div className="w-14 h-14 mx-auto rounded-2xl bg-accent/10 flex items-center justify-center mb-4">
                  <Icon name={item.icon} size={26} className="text-accent" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SOCIAL PROOF BAR ============ */}
      <section className="py-16 bg-gradient-to-r from-accent/10 via-accent/5 to-accent/10 border-y border-accent/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "100K+", label: t("home.socialActiveUsers") },
              { value: "50+", label: t("home.socialCourses") },
              { value: "4.9", label: t("home.socialUserRating") },
              { value: "24/7", label: t("home.socialSupport") },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-3xl md:text-4xl font-bold text-accent font-data">
                  {s.value}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-accent/10 flex items-center justify-center">
            <Icon
              name={isAuthenticated ? "LayoutDashboard" : "Rocket"}
              size={32}
              className="text-accent"
            />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            {isAuthenticated
              ? t("home.ctaAuthTitle")
              : t("home.ctaTitle")}
          </h2>
          <p className="text-muted-foreground text-lg mb-10 max-w-xl mx-auto">
            {isAuthenticated
              ? t("home.ctaAuthSub")
              : t("home.ctaSub")}
          </p>
          <button
            onClick={() =>
              handleNavigate(isAuthenticated ? "dashboard" : "register")
            }
            className="inline-flex items-center gap-2 px-10 py-4 bg-accent hover:bg-accent-dark text-accent-foreground font-bold text-lg rounded-xl transition-colors shadow-glow hover:shadow-glow-lg"
          >
            {isAuthenticated ? (
              <>
                <Icon name="LayoutDashboard" size={20} />
                {t("home.openDashboard")}
              </>
            ) : (
              <>
                {t("home.createFreeAccount")}
                <Icon name="ArrowRight" size={20} />
              </>
            )}
          </button>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
